/**
 * UTM capture helper for Good Atoms.
 *
 * Reads UTM parameters and document.referrer on Landing mount and persists
 * them to localStorage under `goodatoms_utm` so downstream signup / Stripe
 * attribution can read them later. First-touch wins: we never overwrite an
 * existing capture (so an organic homepage hit after a campaign click won't
 * blow away the original campaign attribution).
 */

const STORAGE_KEY = 'goodatoms_utm';

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

type UtmKey = (typeof UTM_KEYS)[number];

export type UtmCapture = {
  [K in UtmKey]?: string;
} & {
  referrer?: string;
  landingPath?: string;
  capturedAt: string;
};

/**
 * Capture UTM params from the current URL and write to localStorage.
 * Safe to call multiple times — first-touch wins, returns the existing
 * capture if one is already stored. SSR-safe (no-ops when window is absent).
 */
export function captureUtm(): UtmCapture | null {
  if (typeof window === 'undefined') return null;

  try {
    const existingRaw = window.localStorage.getItem(STORAGE_KEY);
    if (existingRaw) {
      try {
        const parsed = JSON.parse(existingRaw) as UtmCapture;
        if (parsed && typeof parsed === 'object' && parsed.capturedAt) {
          return parsed;
        }
      } catch {
        // Corrupted entry — fall through and overwrite.
      }
    }

    const params = new URLSearchParams(window.location.search);
    const capture: UtmCapture = {
      capturedAt: new Date().toISOString(),
      landingPath: window.location.pathname,
    };

    let foundAny = false;
    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value && value.trim().length > 0) {
        capture[key] = value.trim().slice(0, 200);
        foundAny = true;
      }
    }

    const referrer = typeof document !== 'undefined' ? document.referrer : '';
    if (referrer && referrer.trim().length > 0) {
      capture.referrer = referrer.trim().slice(0, 500);
      foundAny = true;
    }

    // Only persist if we captured something meaningful — empty landings
    // shouldn't shadow a later first-touch campaign hit.
    if (!foundAny) return null;

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(capture));
    return capture;
  } catch {
    // localStorage can throw in private mode or when quota is exceeded.
    return null;
  }
}

/** Read the stored capture without writing. */
export function readUtm(): UtmCapture | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as UtmCapture;
  } catch {
    return null;
  }
}
